import { ElementRef, Injectable } from '@angular/core';
import { Http2Service } from '../services/http2.service';
import { environment } from '../../environments/environment';
import { IEsriLayer, IFeature, IColumn, ILayer, ILayerSettings, ILayerToAdd, IAssetIdConfig } from './../models';
import { AoiComponent } from './../components/aoi/aoi.component';

@Injectable()
export class UtilsService {

    identify: any;
    aoi: AoiComponent;
    extent: any;
    aoiWindow: ElementRef<any>;
    tooltip: any;
    user: any;
    userSettings: any;
    auth: any;
    aoiRegion: any;
    featureSelected: IFeature;
    streetView: ElementRef<any>;
    streetViewWindow: ElementRef<any>;
    streetViewInitialized: boolean;
    userPermissions: string[] = [];
    videoLogWindow: ElementRef<any>;

    constructor(
        private readonly http2Service: Http2Service
    ) { }

    get mcAuth(): any {
        return document.getElementById('mc-auth');
    }

    get mcMap(): any {
        return document.getElementById('mc-map');
    }

    get esriMap(): any {
        var mcMap = document.getElementById('mc-map');
        return (<any>mcMap).map;
    }

    get esriMapView(): any {
        var mcMap = document.getElementById('mc-map');
        return (<any>mcMap).mapView;
    }

    get layerService(): any {
        return this.mcMap.LayerService;
    }

    get mapService(): any {
        return this.mcMap.MapService;
    }

    get mapLayers(): any[] {
        const mapLayers: any[] = [];
        for (const layer of this.mcMap.map.layers.toArray() as any[]) {
            if (layer.type === 'group' && Symbol.iterator in Object(layer.layers)) {
                for (const sub of layer.layers) {
                    mapLayers.push(sub);
                }
            } else {
                mapLayers.push(layer);
            }
        }
        return mapLayers;
    }

    setupAOISelections() {
        const userSettings = this.userSettings;
        const regionElement = this.aoiRegion.nativeElement;
        if (userSettings && userSettings.counties && userSettings.counties.length && regionElement.counties) {
            const userCountyCodes = userSettings.counties.map(x => x.split('-')[1]);
            this.aoiRegion.nativeElement.selections = regionElement.counties.filter(x => userCountyCodes.indexOf(x.countyCode) >= 0);
        }
    }

    isSelectedFeatureATMSSite(): boolean {
        const tmsSiteColumn = this.getFeatureColumnWithColumnName(this.featureSelected, 'TMS_SITE_NO');
        return !!tmsSiteColumn;
    }

    activateIdentify(activate: boolean) {
        if (activate) {
            this.identify.nativeElement.activate();
        }
        else {
            this.identify.nativeElement.deactivate();
        }
    }

    openAoi() {
        this.aoi.activateTab();
        this.aoiWindow.nativeElement.open();
    }

    loadDeepLink() {
        const layersToAdd = this.getLayersToAddFromUrl();
        if (layersToAdd.length > 0) {
            for (const layerToAdd of layersToAdd) {
                this.addLayer(layerToAdd);
            }
        } else {
            this.openSystemMap();
        }
    }

    openStreetView() {
        if (!this.streetViewInitialized) {
            const streetView: any = document.getElementById('streetView');
            const latLongSelect: any = document.getElementById('latLongSelect');
            const rmsSelect: any = document.getElementById('rmsSelect');

            this.streetViewWindow.nativeElement.addEventListener('openChange', e => {
                streetView.active = e.detail;
            });

            streetView.addEventListener('positionChange', (position) => {
                latLongSelect.setAttribute('lat', position.detail.latitude);
                latLongSelect.setAttribute('long', position.detail.longitude);
                rmsSelect.position = position.detail;
            });

            latLongSelect.addEventListener('latChange', (lat) => {
                streetView.setAttribute('lat', lat.detail);
            });

            latLongSelect.addEventListener('longChange', (long) => {
                streetView.setAttribute('long', long.detail);
            });

            rmsSelect.addEventListener('rmsChange', (rms) => {
                streetView.setRms(rms.detail);
            });
            this.streetViewInitialized = true;
        }
        this.streetViewWindow.nativeElement.open();
    }

    getLayersToAddFromUrl(): ILayerToAdd[] {
        const assetConfigs: IAssetIdConfig[] = environment.aoiSettings.assetIdConfig;
        const tmsSiteConfig = assetConfigs.filter(x => x.fieldName === 'tmsSiteNumber')[0];
        assetConfigs.push({ propName: 'tmsSiteNo', layerId: tmsSiteConfig.layerId, columnName: tmsSiteConfig.columnName });
        const layersToAdd: ILayerToAdd[] = [];
        for (const assetConfig of assetConfigs) {
            const foundParamValue = this.getQueryStringParam(assetConfig.propName);
            if (foundParamValue) {
                layersToAdd.push({ layerId: assetConfig.layerId, columnName: assetConfig.columnName, value: foundParamValue });
            }
        }
        return layersToAdd;
    }

    getLayerByUniqueId(layerId: any): IEsriLayer {
        return this.mapLayers.filter(l => l.id === layerId)[0];
    }

    getRootUrl() {
        const appRootUrl = environment.appPath;
        if (location.href.indexOf('localhost:') >= 0) {
            return '';
        }
        return appRootUrl;
    }

    getCleanValue(value, translation) {
        if (value && value.split !== undefined) {
            let values = value.split('-');
            value = values[0].trim();
        }
        return value;
    }

    getParameterByName(key, url: string = undefined) {
        if (!url) {
            url = window.location.href
        }
        key = key.replace(/[\[\]]/g, '\\$&');
        var regex = new RegExp('[?&]' + key + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }

    addMessage(message: string) {
        let applicationMessage = new CustomEvent('applicationMessage', { detail: { message: message, level: 2, isPrivate: false }, composed: true });
        window.dispatchEvent(applicationMessage);
    }

    httpGet(urlComponents) {
        var url = this.getRestUrlFromUrlComponents(urlComponents);
        return this.http2Service.get<any>(url, { withCredentials: true });
    }

    httpDelete(urlComponents) {
        var url = this.getRestUrlFromUrlComponents(urlComponents);
        return this.http2Service.delete(url, { withCredentials: true });
    }

    httpPost(urlComponents: Array<string>, data: any) {
        var url = this.getRestUrlFromUrlComponents(urlComponents);
        return this.http2Service.post<any>(url, data, { withCredentials: true });
    }

    hex2a(hex) {
        var str = '';
        for (var i = 0; i < hex.length; i += 2)
            str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
        return str;
    }

    postMessage(name: string, dataName?: string, data?: any): void {
        let message: any = { 'name': name };
        if (dataName) {
            message[dataName] = data;
        }
        top.postMessage(message, '*');
    }

    getFeatureColumnWithColumnName(feature: IFeature, columnName: string): IColumn {
        if (feature && feature.sourceLayer) {
            let layer = feature.sourceLayer;
            if (feature.sourceLayer.layer) {
                layer = feature.sourceLayer.layer;
            }
            return this.getLayerColumnMatchingColumnName(layer, columnName);
        }
        return null;
    }

    getAttributeValueMatchingColumnName(columnIdentifierName: string): string {
        const featureColumn = this.getFeatureColumnWithColumnName(this.featureSelected, columnIdentifierName);
        if (featureColumn) {
            const attributes = this.featureSelected.attributes.filter(x => x.columnName === featureColumn.name);
            if (attributes.length) {
                return attributes[0].value;
            }
        }
        return null;
    }

    getLayerColumnMatchingColumnName(layer: ILayer, columnName: string): IColumn {
        if (layer && layer.mapcoreProps && layer.mapcoreProps.columns) {
            const columnsFound = layer.mapcoreProps.columns.filter(x => x.name === columnName);
            if (columnsFound.length > 0) {
                return columnsFound[0];
            }
        }
        return null;
    }

    customizeFeatureSelected(feature: any) {
        this.onlyDisplayColumnsSpecified(feature);
        this.updateColumnAliasesForFeature(feature);
        this.updateFeatureValues(feature);
    }

    onlyDisplayColumnsSpecified(feature: any) {
        if (feature && feature.attributes && feature.sourceLayer && feature.sourceLayer && feature.sourceLayer.layer && feature.sourceLayer.layer.mapcoreProps) {
            const layerColumnListsFound = environment.layerColumnLists.filter(x => x.layerId === feature.sourceLayer.layer.mapcoreProps.parentId);
            if (layerColumnListsFound.length) {
                const layerColumnListFound = layerColumnListsFound[0];
                feature.attributes = feature.attributes.filter(x => layerColumnListFound.columnList.indexOf(x.columnName) >= 0);
            }
        }
    }

    updateColumnAliasesForFeature(feature: any) {
        if (feature && feature.attributes) {
            for (const attribute of feature.attributes) {
                attribute.displayName = this.getTitleCase(attribute.displayName.replaceAll('_', ' '));
                const columnsFound = environment.columnAliasOverrides.filter(x => x.name === attribute.columnName);
                if (columnsFound.length) {
                    const columnFound = columnsFound[0];
                    attribute.displayName = columnFound.alias;
                }
            }
        }
    }

    updateFeatureValues(feature: any) {
        if (feature && feature.attributes && feature.sourceLayer && feature.sourceLayer && feature.sourceLayer.layer && feature.sourceLayer.layer.mapcoreProps) {
            const layerFieldValuesFound = environment.layerColumnValueOverrides.filter(x => x.layerId === feature.sourceLayer.layer.mapcoreProps.parentId);
            if (layerFieldValuesFound.length) {
                const layerFieldValues = layerFieldValuesFound[0];
                for (const fieldValueDescription of layerFieldValues.columnValueDescriptions) {
                    const attributesFound = feature.attributes.filter(x => x.columnName === fieldValueDescription.columnName);
                    if (attributesFound.length) {
                        const attribute = attributesFound[0];
                        for (const valueDescription of fieldValueDescription.valuesAndDescriptions) {
                            const array = valueDescription.split('|');
                            const value = array[0];
                            const description = array[1];
                            if (attribute.value === value) {
                                attribute.value = attribute.value + ' - ' + description;
                            }
                        }
                    }
                }
            }
        }
    }

    async openSystemMap() {
        const maps = await this.mapService.getMaps([environment.completeMapId]);
        if (maps.length) {
            const systemMap = maps[0];
            const mapData = JSON.parse(systemMap.data);
            mapData.extent = null; // remove saved extent before loading.
            systemMap.data = JSON.stringify(mapData);
            await this.mapService.openMap(systemMap);
            
            if (this.mcMap.DeepLinkService.deepLinkParamIsPresent()) {
                window.requestAnimationFrame(() => {
                    this.layerService.moveLayerToTheTop(this.mapLayers[0]);
                });
            }
        }
    }

    async addLayer(layerToAdd: ILayerToAdd) {
        const layerSettings: ILayerSettings = {
            id: layerToAdd.layerId, query: { 'field': layerToAdd.columnName, 'operator': 'IN', 'value': layerToAdd.value }, zoomToLayer: true
        };
        this.layerService.addMapcoreLayersToMap([layerSettings]);
    }

    async getCorridorRoutes(): Promise<string[]> {
        const result = await this.graphqlRequest(`query GetCorridorStateRoutes {
            rmssegments(juris: "1", interstateNetworkIndicator: "Y", hovType: 0 ) {
                stateRouteNumber
            }
        }`, {}, 'GET', 'rmssegments');
        let routeNumbers: string[] = result.map(x => x.stateRouteNumber.toString());
        routeNumbers = routeNumbers.filter((v, i, a) => a.indexOf(v) === i);
        routeNumbers.sort();
        return routeNumbers;
    }

    async graphqlRequest<T = any>(query: string, variables: any, method: string = 'GET', dataPath: string = null) {
        let queryName: string;
        let maxQueryAndVariablesLength = 1300;
        let tQuery = 'query';
        let tMutation = 'mutation';

        const type = query.trim().toLowerCase().indexOf(tQuery) === 0 ? tQuery : tMutation;
        if (method === 'GET' && (query.length + JSON.stringify(variables).length) > maxQueryAndVariablesLength) {
            method = 'POST';
        }
        if (type === tQuery) {
            // find the query name from the query and use that in the querystring to help with debugging to distinguish this request
            queryName = query.substring(query.indexOf(tQuery) + tQuery.length, query.indexOf('{')).trim();
        } else {
            queryName = query.substring(query.indexOf(tMutation) + tMutation.length, query.indexOf('(')).trim();
        }
        // remove any excess whitespace from the query only for GET requests since this is passed on the URL
        if (method === 'GET') {
            query = this.graphqlTrim(query);
        }
        let response: any = {};

        try {
            var graphQLBaseUrl = environment.mapcoreApi + '/graphql';
            response = await this.request<{ data: any }>(`${graphQLBaseUrl}?${queryName}&pdauthAppName=${environment.pdauthAppName}`, {
                body: method !== 'GET' ? {
                    query,
                    variables
                } : undefined,
                method,
                params: method === 'GET' ? {
                    query,
                    variables: JSON.stringify(variables)
                } : undefined
            });
        } catch (e) {
            if (e && !e.message && e.errors && e.errors[0]) {
                e.message = e.errors[0].message;
            }
            throw e;
        }
        if (!dataPath) {
            return <T>response.data;
        } else {
            const parts = dataPath.split('.');
            let result = response;
            if (response.data) {
                result = response.data;
            }
            for (const part of parts) {
                result = result[part];
            }
            return <T>result;
        }
    }

    async request<T>(url: string, config: any): Promise<T> {
        if (config.body) {
            config.body = JSON.stringify(config.body);
        }
        config = {
            headers: {
                'Content-Type': 'application/json'
            },
            params: {},
            ...config
        };

        // remove any trailing slash from the url
        if (url.lastIndexOf('/') === url.length - 1) {
            url = url.substring(0, url.length - 1);
        }

        if (config.method === 'GET') {
            url = url + '&query=' + config.params.query + '&variables=' + config.params.variables;
            return this.http2Service.get<any>(url, { withCredentials: true }).then(result => {
                return result.data;
            });
        } else {
            return this.http2Service.post<any>(url, config.body, { withCredentials: true }).then(result => {
                return result.data;
            });
        }
    }

    getRestUrlFromUrlComponents(urlComponents: Array<string>) {
        urlComponents.forEach((component, index) => {
            urlComponents[index] = component.toString();
        });
        if (urlComponents[0].indexOf('//') < 0) {
            urlComponents.unshift(environment.appPath + '/api');
        }
        return urlComponents.join('/');
    }

    graphqlTrim(text: string) {
        text = text.replace(/\s+/g, ' ');
        return text;
    }

    getCookie(c_name) {
        if (document.cookie.length > 0) {
            let cStart = document.cookie.indexOf(c_name + '=');
            if (cStart != -1) {
                cStart = cStart + c_name.length + 1;
                let cEnd = document.cookie.indexOf(';', cStart);
                if (cEnd == -1) {
                    cEnd = document.cookie.length;
                }
                return unescape(document.cookie.substring(cStart, cEnd));
            }
        }
        return '';
    }

    setCookie(name, value, days) {
        let expires;
        if (days) {
            let date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            expires = '; expires=' + date.toISOString();
        }
        else {
            expires = '';
        }
        document.cookie = name + '=' + value + expires + '; path=/';
    }

    getQueryStringParam(name: string): any {
        return this.getQueryStringParams()[name.toLowerCase()] || null;
    }

    getQueryStringParams() {
        const searchPairs = window.location.search.replace('?', '').replace(/%20/g, ' ').split('&');
        return searchPairs.reduce((acc: any, item: string): any => {
            item = item.replace('%22=%22', '%22%3D%22'); // prevent "=" from splitting
            const keyVal = item.split('=');
            let key = keyVal[0];
            let val = acc[key];

            if (key) {
                key = key.toLowerCase();
            }
            if (val !== undefined && val !== null && !Array.isArray(val)) {
                acc[key] = val = [acc[key]];
            }

            if (keyVal.length > 1) {
                if (Array.isArray(val)) {
                    val.push(keyVal[1]);
                } else {
                    acc[key] = keyVal[1];
                }
            } else {
                if (Array.isArray(val)) {
                    val.push(true);
                } else {
                    acc[key] = true;
                }
            }

            return acc;
        }, {});
    }

    getTitleCase(text: string) {
        if (text) {
            return text.replace(
                /\w\S*/g,
                (x) => {
                    return x.charAt(0).toUpperCase() + x.substr(1).toLowerCase();
                }
            );
        }
        return '';
    }

}